import { useCallback } from 'react';

import type { Row, Table } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { usePermission } from '@/features/_shared/user';
import { useSelectionFunctions } from '@/features/symbol-selector';
import CustomCheckbox from '@/shared/ui/primitives/Checkbox';
import { IndeterminateIcon } from '@/shared/ui/primitives/Checkbox/IndeterminateCheckbox';

import { isRowRestricted } from '../../../lib/isRowRestricted';
import { parseForwardCurveRowToSymbol } from '../../../lib/parseForwardCurveRowToSymbol';

const getSpotSymbol = (row: Row<CatalogItem>): SymbolKey | undefined =>
  row.getValue<SymbolKey | undefined>('Symbol');

const SelectAllCheckbox = ({
  table,
  isForwardCurves
}: {
  table: Table<CatalogItem>;
  isForwardCurves: boolean;
}) => {
  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewCatalogDetails);

  const {
    areAllSymbolsSelected,
    areAnySymbolsSelected,
    addSymbolsToSelection,
    addForwardCurvesToSelection,
    removeSymbolsFromSelection,
    isSpotLocked,
    isForwardCurveLocked
  } = useSelectionFunctions();

  const selectableRows = table
    .getRowModel()
    .rows.filter(row => !isRowRestricted(row, isForwardCurves, isPermissionGranted));

  const targets = isForwardCurves
    ? selectableRows.flatMap(row => parseForwardCurveRowToSymbol(row))
    : selectableRows.flatMap(row => {
        const symbol = getSpotSymbol(row);
        return symbol ? [symbol] : [];
      });

  const allSelected = targets.length > 0 && areAllSymbolsSelected(targets);
  const anySelected = areAnySymbolsSelected(targets);
  const modeLocked = isForwardCurves ? isForwardCurveLocked : isSpotLocked;

  const handleChange = useCallback(() => {
    if (!targets.length) return;
    if (allSelected) {
      removeSymbolsFromSelection(targets);
    } else if (isForwardCurves) {
      addForwardCurvesToSelection(targets);
    } else {
      addSymbolsToSelection(targets);
    }
  }, [
    targets,
    allSelected,
    isForwardCurves,
    removeSymbolsFromSelection,
    addForwardCurvesToSelection,
    addSymbolsToSelection
  ]);

  return (
    <CustomCheckbox
      checked={allSelected}
      disabled={!targets.length}
      indeterminate={!allSelected && anySelected}
      indeterminateIcon={<IndeterminateIcon />}
      modeLocked={modeLocked}
      onLockedClick={handleChange}
      onChange={handleChange}
    />
  );
};

export default SelectAllCheckbox;
